/**
 * AdminDashboard - Full Admin Control Panel
 * ThePedigreeApp
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, CheckCircle, XCircle, Clock, Eye, Search, Filter,
  Upload, Image, FileText, Settings, Shield, AlertCircle,
  TrendingUp, DollarSign, Cat, Baby, ChevronDown, ChevronUp,
  Mail, Phone, MapPin, Calendar, Download, RefreshCw, Edit2, Trash2
} from 'lucide-react';
import { Card, Button, Badge, Alert } from '../shared/UIComponents';
import { cn } from '../../utils/helpers';

const ADMIN_TABS = [
  { id: 'overview', label: 'Overview', icon: TrendingUp },
  { id: 'breeders', label: 'Breeders', icon: Users },
  { id: 'pending', label: 'Pending Approval', icon: Clock },
  { id: 'ads', label: 'Advertisements', icon: Image },
  { id: 'support', label: 'Support Access', icon: Shield },
];

const SAMPLE_PENDING = [
  { id: '1', name: 'Sarah Johnson', email: 'sarah@example.com', phone: '082 123 4567', catteryName: 'Whiskers Pride', registryBody: 'SACC', registryNumber: 'SACC-12345', province: 'Western Cape', appliedDate: '2024-02-01', documents: ['cert.pdf'], status: 'pending' },
  { id: '2', name: 'Mike Peters', email: 'mike@example.com', phone: '083 456 7890', catteryName: 'Blue Diamond', registryBody: 'CASA', registryNumber: 'CASA-67890', province: 'Gauteng', appliedDate: '2024-02-03', documents: ['cert.pdf'], status: 'pending' },
];

const SAMPLE_BREEDERS = [
  { id: '3', name: 'Anna Smith', email: 'anna@example.com', catteryName: 'Royal Felines', registryBody: 'SACC', province: 'KZN', status: 'active', trialEnds: '2024-03-01', totalCats: 12, joinedDate: '2024-01-15' },
  { id: '4', name: 'John Williams', email: 'john@example.com', catteryName: 'Silver Moon', registryBody: 'CFSA', province: 'EC', status: 'trial', trialEnds: '2024-02-28', totalCats: 8, joinedDate: '2024-01-28' },
];

const OverviewStats = () => {
  const stats = [
    { label: 'Total Breeders', value: 156, icon: Users, color: 'bg-blue-500', change: '+12 this month' },
    { label: 'Pending Approval', value: 8, icon: Clock, color: 'bg-yellow-500', change: '3 urgent' },
    { label: 'Active Trials', value: 23, icon: Shield, color: 'bg-purple-500', change: '5 ending soon' },
    { label: 'Total Cats', value: 1247, icon: Cat, color: 'bg-pedigree-500', change: '+89 this month' },
    { label: 'Litters MTD', value: 34, icon: Baby, color: 'bg-pink-500', change: 'R51,000 fees' },
    { label: 'Revenue MTD', value: 'R51K', icon: DollarSign, color: 'bg-green-500', change: '+18%' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      {stats.map((stat, i) => (
        <Card key={i} className="p-4">
          <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center mb-3', stat.color)}>
            <stat.icon className="w-5 h-5 text-white" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
          <p className="text-sm text-gray-500">{stat.label}</p>
          <p className="text-xs text-gray-400 mt-1">{stat.change}</p>
        </Card>
      ))}
    </div>
  );
};

const PendingCard = ({ breeder, onApprove, onReject }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Card className="overflow-hidden">
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{breeder.name}</h3>
              <p className="text-sm text-gray-500">{breeder.catteryName}</p>
            </div>
          </div>
          <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><p className="text-gray-500">Registry</p><p className="font-medium">{breeder.registryBody}</p></div>
          <div><p className="text-gray-500">Province</p><p className="font-medium">{breeder.province}</p></div>
          <div><p className="text-gray-500">Applied</p><p className="font-medium">{new Date(breeder.appliedDate).toLocaleDateString('en-ZA')}</p></div>
          <div><p className="text-gray-500">Docs</p><p className="font-medium">{breeder.documents?.length || 0} files</p></div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <button onClick={() => setExpanded(!expanded)} className="text-sm text-pedigree-600 hover:underline flex items-center gap-1">
            {expanded ? 'Hide' : 'Details'} {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <div className="flex gap-2">
            <Button size="sm" className="bg-red-500 hover:bg-red-600" onClick={() => onReject?.(breeder.id)}><XCircle className="w-4 h-4" /> Reject</Button>
            <Button size="sm" className="bg-green-500 hover:bg-green-600" onClick={() => onApprove?.(breeder.id)}><CheckCircle className="w-4 h-4" /> Approve</Button>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="border-t bg-gray-50 p-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><p className="flex items-center gap-2"><Mail className="w-4 h-4 text-gray-400" />{breeder.email}</p></div>
            <div><p className="flex items-center gap-2"><Phone className="w-4 h-4 text-gray-400" />{breeder.phone}</p></div>
          </div>
        </div>
      )}
    </Card>
  );
};

const BreederTable = ({ breeders, onAccess }) => (
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Breeder</th>
          <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Cattery</th>
          <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
          <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Cats</th>
          <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y">
        {breeders.map((b) => (
          <tr key={b.id} className="hover:bg-gray-50">
            <td className="px-4 py-3"><p className="font-medium">{b.name}</p><p className="text-sm text-gray-500">{b.email}</p></td>
            <td className="px-4 py-3">{b.catteryName}</td>
            <td className="px-4 py-3"><Badge className={b.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}>{b.status}</Badge></td>
            <td className="px-4 py-3">{b.totalCats}</td>
            <td className="px-4 py-3 text-right"><Button size="sm" onClick={() => onAccess?.(b)}><Shield className="w-4 h-4" /> Access</Button></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [pending, setPending] = useState(SAMPLE_PENDING);
  const [breeders] = useState(SAMPLE_BREEDERS);

  const handleApprove = (id) => { if (confirm('Approve? They get 30-day trial.')) setPending(p => p.filter(b => b.id !== id)); };
  const handleReject = (id) => { const r = prompt('Reason:'); if (r) setPending(p => p.filter(b => b.id !== id)); };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div><h1 className="text-3xl font-display font-bold">Admin Dashboard</h1><p className="text-gray-300">ThePedigreeApp Control Panel</p></div>
          <Badge className="bg-green-500 text-white"><span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse" />Online</Badge>
        </div>
      </div>
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-1 py-2 overflow-x-auto">
          {ADMIN_TABS.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={cn('flex items-center gap-2 px-4 py-2 rounded-lg font-medium', activeTab === tab.id ? 'bg-pedigree-100 text-pedigree-700' : 'text-gray-600 hover:bg-gray-100')}>
              <tab.icon className="w-4 h-4" />{tab.label}
              {tab.id === 'pending' && pending.length > 0 && <Badge className="bg-yellow-500 text-white text-xs">{pending.length}</Badge>}
            </button>
          ))}
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && <OverviewStats />}
        {activeTab === 'pending' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold">Pending Approvals</h2>
            {pending.length === 0 ? <Card className="p-12 text-center"><CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" /><p>All caught up!</p></Card> : pending.map(b => <PendingCard key={b.id} breeder={b} onApprove={handleApprove} onReject={handleReject} />)}
          </div>
        )}
        {activeTab === 'breeders' && <Card><BreederTable breeders={breeders} onAccess={(b) => alert(`Accessing ${b.catteryName}`)} /></Card>}
        {activeTab === 'ads' && <Card className="p-8 text-center"><Image className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p>Ad Manager - Coming Soon</p></Card>}
        {activeTab === 'support' && (
          <div className="space-y-4">
            <Alert type="warning"><Shield className="w-5 h-5" /> Support Access Mode - All actions logged</Alert>
            <Card><BreederTable breeders={breeders} onAccess={(b) => { if (confirm(`Access ${b.name}'s account?`)) alert('Accessing...'); }} /></Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
